import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { CarouselComponent } from 'src/app/layouts/carousel/carousel.component';
import { PaginationComponent } from 'src/app/components/pagination/pagination.component';
import { GetCandidateInJobOffer, GetCandidate } from 'src/app/models/candidate/get-candidate';
import { GetJobOffer } from 'src/app/models/job-offer/get-job-offer';
import { GetUserFilter } from 'src/app/models/security/get-user-filter';
import { Paged } from 'src/app/models/shared/paged';
import { AlertService } from 'src/app/services/alert.service';
import { AuthService } from 'src/app/services/auth.service';
import { CandidateService } from 'src/app/services/candidate.service';
import { FileStorageService } from 'src/app/services/file-storage.service';
import { JobApplicationService } from 'src/app/services/job-application.service';
import { JobOfferService } from 'src/app/services/job-offer.service';
import { UserService } from 'src/app/services/user.service';
import { GetResumeDetail } from 'src/app/models/resume/resume';
import { ResumeService } from 'src/app/services/resume.service';
import { RequestJobApplicationMatch, GetJobApplicationMatch } from 'src/app/models/JobApplications/jobApplication';
import Swal from 'sweetalert2';
import { ViewModalEnum } from 'src/app/enums/operation';
import { ApplicationStatusEnum, ApplicationStatusUtils } from 'src/app/enums/application-status.enum';
import { Workbook, ImagePosition } from 'exceljs';
import { saveAs } from 'file-saver';
import { ViewTrackerDirective } from 'src/app/directives/view-tracker.directive';
import { ViewType } from 'src/app/services/view-tracking.service';

@Component({
  selector: 'app-job-offer-details-companies',
  standalone: true,
  imports: [CommonModule, RouterModule, PaginationComponent, FormsModule, ViewTrackerDirective],
  templateUrl: './job-offer-details-companies.component.html',
  styleUrl: './job-offer-details-companies.component.css'
})
export class JobOfferDetailsCompaniesComponent implements OnInit {
  // Servicios inyectados
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly jobOfferService = inject(JobOfferService);
  private readonly candidateService = inject(CandidateService);
  private readonly jobApplicationService = inject(JobApplicationService);
  private readonly fileService = inject(FileStorageService);
  private readonly alertService = inject(AlertService);
  private readonly authService = inject(AuthService);
  private readonly toastr = inject(ToastrService);
  private readonly resumeService = inject(ResumeService);
  private readonly userService = inject(UserService);

  // Variables reactivas
  jobOffer = signal<GetJobOffer | undefined>(undefined);
  candidates = signal<(GetCandidateInJobOffer & { applicationStatus?: string; statusId?: ApplicationStatusEnum; matchPercent?: number })[]>([]);
  jobOfferId: number = 0;
  candidateMatches = signal<Map<number, number>>(new Map<number, number>());
  isLoading = signal(false);
  hasError = signal(false);
  errorMessage = signal('');

  // Candidate CV - Variables for modal and tracking
  selectedResumeId = signal<number | null>(null);     // Specific resume being displayed
  selectedCandidateId = signal<number | null>(null);  // Candidate ID
  selectedPublicResumeId = signal<number | null>(null); // Public resume for unified tracking
  resumeData = signal<GetResumeDetail | null>(null);
  isLoadingResume = signal(false);
  hasResumeError = signal(false);
  showResumeModal = signal(false);

  // Señales para el modal de comentario
  showCommentModal = signal<boolean>(false);
  pendingStatus = signal<string>('');
  statusComment = signal<string>('');

  // Mapa para rastrear los candidatos en proceso de actualización y sus estados
  loadingCandidates = signal<Map<number, { loading: boolean; error: boolean; }>>(new Map());
  pendingCandidate: (GetCandidateInJobOffer & { applicationStatus?: string; statusId?: ApplicationStatusEnum }) | null = null;
  pendingStatusId: ApplicationStatusEnum | null = null;

  // Paginación
  page = signal(1);
  pageSize = signal(10);
  totalItems = signal(0);
  totalPages = signal(0);

  // Filtros de búsqueda
  filterValue = signal('');

  // Tracking properties
  ViewType = ViewType;

  viewEnums = ViewModalEnum;

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.jobOfferId = +params['jobOfferId'];
      if (this.jobOfferId) {
        this.loadJobOfferDetails();
        this.loadCandidates();
      } else {
        this.router.navigate(['/companies/job-offers']);
      }
    });
  }

  loadJobOfferDetails(): void {
    this.isLoading.set(true);
    this.hasError.set(false);

    this.jobOfferService.getById(this.jobOfferId).subscribe({
      next: (data) => {
        if (data) {
          this.jobOffer.set(data);
        } else {
          this.hasError.set(true);
          this.errorMessage.set('No se encontró la oferta laboral solicitada.');
        }
      },
      error: (error) => {
        this.hasError.set(true);
        this.errorMessage.set('Error al cargar los detalles de la oferta laboral. Por favor, intente nuevamente.');
        this.alertService.showErrorAlert(error);
      }
    }).add(() => {
      this.isLoading.set(false);
    });
  }

  loadCandidates(page: number = 1): void {
    this.isLoading.set(true);
    this.page.set(page);

    const filter: GetUserFilter = {
      page: this.page(),
      pageSize: this.pageSize(),
      filterKey: 'jobOfferId',
      filterValue: this.jobOfferId.toString()
    };

    this.candidateService.getCandidatesInJobOffer(filter).subscribe({
      next: (response: Paged<GetCandidateInJobOffer>) => {
        const candidatesWithPhoto = response.items.map(candidate => {
          let photoUrl = 'assets/Images/user.png';
          if (candidate.photoFileId) {
            photoUrl = this.fileService.getFileUrl(candidate.photoFileId.toString());
          }

          return {
            ...candidate,
            photoUrl: photoUrl
          };
        });

        this.candidates.set(candidatesWithPhoto);
        this.totalItems.set(response.totalItems);
        this.totalPages.set(Math.ceil(response.totalItems / this.pageSize()));
        
        // Una vez cargados los candidatos, obtener los porcentajes de match
        if (candidatesWithPhoto.length > 0) {
          this.loadCandidateMatches();
        }
      },
      error: (error) => {
        this.alertService.showErrorAlert('Error al cargar los candidatos. Por favor, intente nuevamente.');
        console.error('Error al cargar candidatos:', error);
      }
    }).add(() => {
      this.isLoading.set(false);
    });
  }
  
  /**
   * Carga los porcentajes de match para los candidatos de la oferta laboral
   */
  loadCandidateMatches(): void {
    // Extraer los IDs de las aplicaciones de trabajo
    const jobApplicationIds: number[] = this.candidates()
      .filter(c => c.jobApplicationId !== undefined && c.jobApplicationId !== null)
      .map(c => c.jobApplicationId as number);
    
    if (jobApplicationIds.length === 0) {
      return; // No hay aplicaciones para consultar
    }
    
    const request: RequestJobApplicationMatch = {
      jobOfferId: this.jobOfferId,
      jobApplicationIds: jobApplicationIds
    };
    
    this.jobApplicationService.requestJobApplicationMatch(request).subscribe({
      next: (matches: GetJobApplicationMatch[]) => {
        // Crear un mapa para almacenar los porcentajes de match por ID de aplicación
        const matchMap = new Map<number, number>();
        
        // Llenar el mapa con los porcentajes de match
        matches.forEach(match => {
          matchMap.set(match.jobApplicationId, match.matchPercent);
        });
        
        // Actualizar el signal con el mapa de matches
        this.candidateMatches.set(matchMap);
        
        // Actualizar los candidatos con sus porcentajes de match
        const updatedCandidates = this.candidates().map(candidate => {
          return {
            ...candidate,
            matchPercent: candidate.jobApplicationId ? (matchMap.get(candidate.jobApplicationId) || 0) : 0
          };
        });
        
        this.candidates.set(updatedCandidates);
      },
      error: (error) => {
        console.error('Error al obtener los porcentajes de match:', error);
      }
    });
  }
  

  onPageChange(page: number): void {
    this.loadCandidates(page);
  }

  onPageSizeChange(pageSize: number): void {
    this.pageSize.set(pageSize);
    this.loadCandidates();
  }

  formatDate(date: string | null | undefined): string {
    if (!date) return 'No especificada';
    return new Date(date).toLocaleDateString('es-DO');
  }

  goBack(): void {
    this.router.navigate(['/companies/job-offers']);
  }

  viewCandidateResume(userId: number, resumeId: number | null): void {
    if (resumeId !== null) {
      const nonNullResumeId: number = resumeId;
      this.selectedCandidateId.set(userId);
      this.selectedResumeId.set(nonNullResumeId);
      this.showResumeModal.set(true);
      
      // Get candidate's publicResumeId for unified view tracking
      this.userService.getCandidate(userId).subscribe({
        next: (candidate: GetCandidate) => {
          const publicResumeId = candidate.publicResumeId || candidate.professionalProfileId || nonNullResumeId;
          this.selectedPublicResumeId.set(publicResumeId);
        },
        error: (error: any) => {
          console.warn('Error obteniendo publicResumeId, usando resumeId original:', nonNullResumeId);
          this.selectedPublicResumeId.set(nonNullResumeId);
        }
      });
      
      this.loadResumeDetail(nonNullResumeId);
    } else {
      this.toastr.warning('El candidato no tiene un currículum disponible');
    }
  }

  /**
   * Carga los detalles del CV seleccionado
   * @param resumeId ID del currículum a cargar
   */
  loadResumeDetail(resumeId: number): void {
    this.isLoadingResume.set(true);
    this.hasResumeError.set(false);

    this.resumeService.getResumeDetail(resumeId).subscribe({
      next: (data) => {
        this.resumeData.set(data);
      },
      error: (error) => {
        this.hasResumeError.set(true);
        this.toastr.error('Error al cargar el currículum vitae. Inténtelo de nuevo más tarde.');
        console.error('Error al cargar el CV:', error);
      },
      complete: () => {
        this.isLoadingResume.set(false);
      }
    });
  }

  /**
   * Cierra el modal del CV
   */
  closeResumeModal(): void {
    this.showResumeModal.set(false);
    this.selectedResumeId.set(null);
    this.selectedCandidateId.set(null);
    this.selectedPublicResumeId.set(null);
    this.resumeData.set(null);
  }
  
  /**
   * Imprime el currículum del candidato
   */
  printResume(): void {
    const resumeId = this.selectedResumeId();
    if (!resumeId) {
      this.toastr.warning('No hay currículum seleccionado para imprimir');
      return;
    }
    
    this.isLoadingResume.set(true);
    this.resumeService.generateResumePdf(resumeId).subscribe({
      next: (response) => {
        const file = new Blob([response], { type: 'application/pdf' }); // Crear el blob del PDF
        const fileURL = URL.createObjectURL(file); // Crear la URL del blob

        // Check if we're in a browser environment
        if (typeof document !== 'undefined') {
          // Buscar o crear el iframe para mostrar el PDF
          let iframe = document.getElementById('docPrintDocumento') as HTMLIFrameElement;
          if (!iframe) {
            // Si no existe el iframe, crearlo y agregarlo al DOM
            iframe = document.createElement('iframe');
            iframe.id = 'docPrintDocumento';
            document.body.appendChild(iframe);
          }
          
          iframe.setAttribute('src', fileURL);

          // Mostrar el modal con el PDF
          const modalDocumento = document.getElementById('modalDocumento');
          if (modalDocumento && typeof window !== 'undefined') {
            const bsModal = new (window as any).bootstrap.Modal(modalDocumento);
            bsModal.show();
          } else {
            // Si no existe el modal, abrir el PDF en una nueva ventana
            if (typeof window !== 'undefined') {
              window.open(fileURL, '_blank');
            }
          }
        }
      },
      error: (error) => {
        this.toastr.error('Error al generar el PDF del currículum');
        console.error('Error al generar PDF:', error);
      },
      complete: () => {
        this.isLoadingResume.set(false);
      }
    });
  }

  /**
   * Obtener URL de la foto del perfil
   */
  getPhotoUrl(photoFileId: number | null): string {
    if (photoFileId) {
      return this.fileService.getFileUrl(photoFileId.toString());
    }
    return 'assets/Images/user.png';
  }

  /**
   * Calcula los años totales de experiencia laboral
   */
  calculateExperienceYears(employments: any[]): string {
    if (!employments || employments.length === 0) {
      return 'Sin experiencia';
    }

    const totalMonths = employments.reduce((total, emp) => {
      const startDate = new Date(emp.startDate);
      const endDate = emp.currentJob ? new Date() : (emp.endDate ? new Date(emp.endDate) : new Date());

      const months = (endDate.getFullYear() - startDate.getFullYear()) * 12 +
        (endDate.getMonth() - startDate.getMonth());

      return total + months;
    }, 0);

    const years = Math.floor(totalMonths / 12);
    const remainingMonths = totalMonths % 12;

    if (years > 0 && remainingMonths > 0) {
      return `${years} año${years > 1 ? 's' : ''} y ${remainingMonths} mes${remainingMonths > 1 ? 'es' : ''}`;
    } else if (years > 0) {
      return `${years} año${years > 1 ? 's' : ''}`;
    } else {
      return `${remainingMonths} mes${remainingMonths > 1 ? 'es' : ''}`;
    }
  }

  /**
   * Obtiene el estado actual de la aplicación del candidato como ID del enum
   */
  getApplicationStatusId(candidate: GetCandidateInJobOffer & { applicationStatus?: string; statusId?: ApplicationStatusEnum }): ApplicationStatusEnum {
    // Si el candidato ya tiene un statusId asignado, devolverlo directamente
    if (candidate.jobApplicationStatusId) {
      return candidate.jobApplicationStatusId;
    }
    // Si tiene un statusId temporal asignado (para compatibilidad con la interfaz)
    if (candidate.statusId) {
      return candidate.statusId;
    }
    // Si tiene un string de estado, convertirlo a ID
    if (candidate.applicationStatus) {
      return ApplicationStatusUtils.getStatusId(candidate.applicationStatus);
    }
    // Valor predeterminado
    return ApplicationStatusEnum.Pendiente;
  }

  /**
   * Obtiene el nombre del estado actual de la aplicación del candidato
   */
  getApplicationStatusName(candidate: GetCandidateInJobOffer & { applicationStatus?: string; statusId?: ApplicationStatusEnum }): string {
    return ApplicationStatusUtils.getStatusName(this.getApplicationStatusId(candidate));
  }

  /**
   * Actualiza el estado de la aplicación de un candidato usando el nombre del estado
   * @deprecated Use updateCandidateStatusById instead
   */
  updateCandidateStatus(candidate: GetCandidateInJobOffer & { applicationStatus?: string; }, newStatus: string): void {
    // Convertir el string del estado al valor del enum
    const statusId = ApplicationStatusEnum[newStatus as keyof typeof ApplicationStatusEnum];
    if (!statusId) {
      this.toastr.error(`Estado inválido: ${newStatus}`);
      return;
    }
    
    // Usar el nuevo método que trabaja con IDs
    this.updateCandidateStatusById(candidate, statusId);
  }

  /**
   * Actualiza el estado de la aplicación de un candidato usando el ID del enum
   */
  updateCandidateStatusById(candidate: GetCandidateInJobOffer & { applicationStatus?: string; statusId?: ApplicationStatusEnum }, statusId: ApplicationStatusEnum): void {
    // Verificar que el candidato tenga un ID de aplicación válido
    if (!candidate.jobApplicationId) {
      this.toastr.error('No se puede actualizar el estado: ID de aplicación no válido');
      return;
    }

    // Verificar si el candidato ya está en proceso de actualización
    const jobApplicationId = candidate.jobApplicationId;
    const currentLoadingMap = this.loadingCandidates();
    if (currentLoadingMap.get(jobApplicationId)?.loading) {
      // Ya está en proceso, no hacer nada
      return;
    }

    // Obtener el nombre del estado para mostrar en la interfaz
    const statusName = ApplicationStatusUtils.getStatusName(statusId);

    // Si el estado es 'Otro', mostrar modal para comentario
    if (statusId === ApplicationStatusEnum.Otro) {
      this.pendingCandidate = candidate;
      this.pendingStatusId = statusId;
      this.pendingStatus.set(statusName);
      this.statusComment.set('');
      this.showCommentModal.set(true);
      return;
    }

    // Para otros estados, mostrar confirmación con SweetAlert
    Swal.fire({
      title: 'Confirmar cambio de estado',
      html: `¿Está seguro que desea cambiar el estado de <strong>${candidate.firstName} ${candidate.lastName}</strong> a <strong>${statusName}</strong>?<br><br>El candidato será notificado vía correo electrónico sobre esta decisión.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sí, cambiar estado',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        // Proceder con la actualización del estado
        this.processStatusUpdate(candidate, statusId, statusName);
      }
    });
  }

  /**
   * Cancela la actualización de estado y cierra el modal
   */
  cancelStatusUpdate(): void {
    this.showCommentModal.set(false);
    this.pendingCandidate = null;
    this.pendingStatusId = null;
    this.statusComment.set('');
  }

  /**
   * Aplica la actualización de estado con el comentario ingresado
   */
  applyStatusUpdate(): void {
    if (this.pendingCandidate && this.pendingStatusId) {
      const statusName = ApplicationStatusUtils.getStatusName(this.pendingStatusId);
      // Asegurarse de que el comentario sea una cadena de texto
      const comment = this.statusComment() || '';
      
      // Guardar una referencia local al candidato para evitar problemas de tipo
      const candidate = this.pendingCandidate;
      const statusId = this.pendingStatusId;
      
      // Mostrar confirmación con SweetAlert
      Swal.fire({
        title: 'Confirmar cambio de estado',
        html: `¿Está seguro que desea cambiar el estado de <strong>${candidate.firstName} ${candidate.lastName}</strong> a <strong>${statusName}</strong> con el comentario proporcionado?<br><br>El candidato será notificado vía correo electrónico sobre esta decisión.`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Sí, cambiar estado',
        cancelButtonText: 'Cancelar'
      }).then((result) => {
        if (result.isConfirmed) {
          // Proceder con la actualización del estado
          this.processStatusUpdate(candidate, statusId, statusName, comment);
          this.showCommentModal.set(false);
          this.pendingCandidate = null;
          this.pendingStatusId = null;
        }
      });
    }
  }

  /**
   * Cierra cualquier modal activo
   */
  closeActiveModal(): void {
    if (this.showResumeModal()) {
      this.closeResumeModal();
    }
    if (this.showCommentModal()) {
      this.cancelStatusUpdate();
    }
  }

  /**
   * Exporta la lista de candidatos a un archivo CSV
   */
  exportCandidatesToCsv(): void {
    if (!this.candidates().length || !this.jobOffer()) {
      this.toastr.warning('No hay candidatos para exportar');
      return;
    }

    // Crear encabezados del CSV
    const headers = [
      'ID Oferta', 'Título Oferta', 
      'ID Candidato', 'ID Aplicación', 'Nombre', 'Apellido', 
      'Email', 'Teléfono', 'Estado', 'Match', 'Fecha Aplicación', 'URL Foto'
    ];

    // Preparar los datos de los candidatos
    const csvData = this.candidates().map(candidate => {
      return [
        this.jobOfferId, // ID de la oferta
        this.jobOffer()?.title || '', // Título de la oferta
        candidate.userId, // ID del candidato
        candidate.jobApplicationId || '', // ID de la aplicación
        candidate.firstName || '', // Nombre
        candidate.lastName || '', // Apellido
        candidate.email || '', // Email
        candidate.primaryPhone || candidate.cellPhone || 'No disponible', // Teléfono
        this.getApplicationStatusName(candidate), // Estado
        `${candidate.matchPercent || 0}%`, // Match
        candidate.personCreateDate ? this.formatDate(candidate.personCreateDate) : 'No disponible', // Fecha de aplicación (usando fecha de creación como aproximación)
        candidate.photoUrl || 'No disponible' // URL de la foto del candidato
      ];
    });

    // Unir encabezados y datos
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(cell => 
        // Escapar comas y comillas en los valores
        typeof cell === 'string' ? `"${cell.replace(/"/g, '""')}"` : cell
      ).join(','))
    ].join('\n');

    // Crear un blob con los datos CSV incluyendo BOM para UTF-8
    // El BOM (0xEF, 0xBB, 0xBF) ayuda a Excel a reconocer automáticamente la codificación UTF-8
    const BOM = new Uint8Array([0xEF, 0xBB, 0xBF]);
    const textEncoder = new TextEncoder();
    const csvContentEncoded = textEncoder.encode(csvContent);
    
    // Combinar BOM y contenido CSV
    const csvWithBOM = new Uint8Array(BOM.length + csvContentEncoded.length);
    csvWithBOM.set(BOM, 0);
    csvWithBOM.set(csvContentEncoded, BOM.length);
    
    // Crear un blob con los datos CSV y codificación UTF-8
    const blob = new Blob([csvWithBOM], { type: 'text/csv;charset=utf-8;' });
    
    // Check if we're in a browser environment
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
      // Crear URL para descargar
      const url = window.URL.createObjectURL(blob);
      
      // Crear elemento de enlace para descargar
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `candidatos_oferta_${this.jobOfferId}_${new Date().toISOString().slice(0,10)}.csv`);
      link.style.visibility = 'hidden';
      
      // Agregar al DOM, hacer clic y eliminar
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      this.toastr.success('Archivo CSV generado correctamente');
    } else {
      this.toastr.error('La descarga no está disponible en este entorno');
    }
  }

  async exportCandidatesToXlsx(): Promise<void> {
    if (!this.candidates().length) {
      this.toastr.warning('No hay candidatos para exportar');
      return;
    }
  
    const wb = new Workbook();
    const ws = wb.addWorksheet('Candidatos');
  
    /* 1. Encabezados */
    const headers = [
      'ID Oferta', 'Título Oferta', 'ID Candidato', 'ID Aplicación',
      'Nombre', 'Apellido', 'Email', 'Teléfono',
      'Estado', 'Match', 'Fecha Aplicación', 'Foto'      // «Foto» será la columna de imagen
    ];
    ws.addRow(headers);
    ws.getRow(1).font = { bold: true };
  
    /* 2. Añadir filas + descargar/fijar cada foto */
    const imagePromises = this.candidates().map(async (cand, i) => {
      // ---- A. Datos textuales
      const row = ws.addRow([
        this.jobOfferId,
        this.jobOffer()?.title || '',
        cand.userId,
        cand.jobApplicationId || '',
        cand.firstName || '',
        cand.lastName || '',
        cand.email || '',
        cand.primaryPhone || cand.cellPhone || 'No disponible',
        this.getApplicationStatusName(cand),
        `${cand.matchPercent || 0}%`,
        cand.personCreateDate ? this.formatDate(cand.personCreateDate) : 'No disponible',
        ''                                // celda foto (se deja vacía)
      ]);
  
      // ---- B. Descargar imagen (si existe) y pegarla
      if (cand.photoUrl) {
        try {
          const blob = await fetch(cand.photoUrl, { mode: 'cors' }).then(r => r.blob());
          const arrayBuffer = await blob.arrayBuffer();
          const imgId = wb.addImage({
            buffer: arrayBuffer,
            extension: blob.type.includes('png') ? 'png' : 'jpeg'
          });
  
          /* Posicionar la imagen:
             - column = headers.length (11)    (ExcelJS usa índices base‑1)
             - row = current Excel row number  */
          const colIdx = headers.length;
          const excelRow = row.number;
          const dims: ImagePosition = {
            tl: { col: colIdx - 1, row: excelRow - 1 },   // top‑left
            ext: { width: 100, height: 100 }                // tamaño miniatura
          };
          ws.addImage(imgId, dims);
  
          // Ajusta altura de la fila para que quepa la foto
          row.height = 100;
        } catch (err) {
          console.error('Error bajando foto', err);
        }
      }
    });
  
    await Promise.all(imagePromises);
  
    /* 3. Ancho de columnas */
    ws.columns.forEach(c => c.width = 18);
  
    /* 4. Generar archivo y descargar */
    const buffer = await wb.xlsx.writeBuffer();
    const blobXlsx = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blobXlsx, `candidatos_oferta_${this.jobOfferId}_${new Date().toISOString().slice(0,10)}.xlsx`);
  
    this.toastr.success('Archivo XLSX generado correctamente');
  }

  /**
   * Procesa la actualización del estado de un candidato
   */
  private processStatusUpdate(
    candidate: GetCandidateInJobOffer & { applicationStatus?: string;  },
    statusId: ApplicationStatusEnum,
    statusName: string,
    comment: string = ''
  ): void {
    const jobApplicationId = candidate.jobApplicationId || 0;
    if (jobApplicationId === 0) {
      this.toastr.error('ID de aplicación no válido');
      return;
    }

    // Marcar el candidato como en proceso de carga
    const currentLoadingMap = new Map(this.loadingCandidates());
    currentLoadingMap.set(jobApplicationId, { loading: true, error: false });
    this.loadingCandidates.set(currentLoadingMap);

    // Actualizar optimistamente la UI
    const currentCandidates = this.candidates();
    const updatedCandidates = currentCandidates.map(c => {
      if (c.jobApplicationId === jobApplicationId) {
        return {
          ...c,
          applicationStatus: statusName,
          jobApplicationStatusId: statusId
        };
      }
      return c;
    });
    this.candidates.set(updatedCandidates);

    // Llamar al servicio para actualizar el estado
    this.jobApplicationService.updateJobApplicationStatus(
      jobApplicationId,
      statusId,
      comment
    ).subscribe({
      next: () => {
        // Actualizar el mapa de carga para indicar éxito
        const updatedLoadingMap = new Map(this.loadingCandidates());
        updatedLoadingMap.delete(jobApplicationId); // Eliminar el estado de carga
        this.loadingCandidates.set(updatedLoadingMap);
        
        this.toastr.success(`Estado de ${candidate.firstName} ${candidate.lastName} actualizado a "${statusName}"${comment ? ' con comentario adicional' : ''}`);
      },
      error: (error) => {
        // Revertir los cambios en la UI
        this.candidates.set(currentCandidates);
        
        // Actualizar el mapa de carga para indicar error
        const updatedLoadingMap = new Map(this.loadingCandidates());
        updatedLoadingMap.set(jobApplicationId, { loading: false, error: true });
        this.loadingCandidates.set(updatedLoadingMap);
        
        this.toastr.error('Error al actualizar el estado del candidato');
        this.alertService.showErrorAlert(error);
        
        // Eliminar el estado de error después de 3 segundos
        setTimeout(() => {
          const finalLoadingMap = new Map(this.loadingCandidates());
          finalLoadingMap.delete(jobApplicationId);
          this.loadingCandidates.set(finalLoadingMap);
        }, 3000);
      }
    });
  }
}
